/**
 * Global "org needs attention" banner.
 *
 * Renders at the top of every dashboard page (inside DashboardShell)
 * for owners + admins on a team that has at least one org with an
 * unresolved low-balance alert. Members never see it — the API
 * returns an empty list for them, so the component renders nothing.
 *
 * Wiring:
 *   - auth-worker fires notifyOwnersOnLowBalance when preCheckBalance
 *     rejects a billable call. That helper atomically claims an alert
 *     event by setting orgs.last_low_balance_alert_at = now.
 *   - Every credit-add path in the user-app + admin-app api workers
 *     (coupons, transfers, IAP webhooks, Stripe webhooks, admin grants)
 *     resets orgs.last_low_balance_alert_at = NULL.
 *   - This banner polls GET /api/billing/alerts on mount + on every
 *     navigation, so once the owner tops up the org the banner
 *     disappears within one page transition.
 *
 * Dismissal: per-session via sessionStorage. The owner can dismiss the
 * banner if they're going to fix it later; it reappears next sign-in
 * if the alert is still standing. The dismissal does NOT clear the
 * server-side alert latch — only adding credits does that.
 */

import { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router';
import { AlertTriangle, X } from 'lucide-react';

interface Alert {
  id: string;
  external_id: string;
  name: string | null;
  balance_cents: number;
  free_credit_cents: number;
  alerted_at: string;
}

const DISMISSED_KEY = 'oak-org-alert-dismissed';

export default function OrgAlertBanner() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();

  // Re-fetch on navigation. The alert list updates whenever:
  //   - the user tops up (banner should disappear)
  //   - a new org just hit empty (banner should appear or grow)
  // Both flow through page navigations in practice, so location.pathname
  // is the simplest trigger.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/billing/alerts');
        if (!res.ok) return; // 401/403 = silently empty (member, signed-out, etc.)
        const data = (await res.json()) as { alerts?: Alert[] };
        if (!cancelled) setAlerts(data.alerts || []);
      } catch {
        // Network error — quietly omit the banner this navigation,
        // try again next one.
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname]);

  // Per-session dismissal. Cleared on new tab / new sign-in, so the
  // owner gets a fresh nudge each session until the alert is resolved
  // server-side via a credit add.
  useEffect(() => {
    if (typeof sessionStorage === 'undefined') return;
    setDismissed(sessionStorage.getItem(DISMISSED_KEY) === '1');
  }, []);

  if (dismissed || alerts.length === 0) return null;

  // Single-alert: name the org inline so the owner knows exactly which
  // one to fund. Multi-alert: collapse to a count. Both link to
  // /dashboard/billing — the page where they can act.
  const first = alerts[0];
  const orgLabel = first.name || first.external_id;
  const message = alerts.length === 1
    ? `${orgLabel} is out of credits and is blocking API calls.`
    : `${alerts.length} orgs are out of credits and blocking API calls.`;

  function handleDismiss() {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISSED_KEY, '1'); } catch {}
  }

  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.65rem 1rem',
        background: 'var(--error-bg)',
        borderBottom: '1px solid var(--error-border)',
        color: 'var(--error-text)',
        fontSize: '0.875rem',
        lineHeight: 1.4,
      }}
    >
      <AlertTriangle size={16} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <strong>{message}</strong>{' '}
        <Link
          to="/dashboard/billing"
          style={{ color: 'var(--error-text)', textDecoration: 'underline', fontWeight: 600 }}
        >
          Add credits
        </Link>
      </span>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss for this session"
        title="Dismiss for this session"
        style={{
          background: 'none',
          border: 'none',
          color: 'inherit',
          opacity: 0.7,
          cursor: 'pointer',
          padding: 0,
          display: 'flex',
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
